import { apiRequestManager } from '../api/api-request-manager';
import { testIsUndefined } from '../../lib/test-and-assert/test-base';
import { satGlobals } from '../../qaamgo/sat/helper/sat-globals';
import { assertIsNonEmptyString, assertIsObject, assertObjectHasKey } from '../../lib/test-and-assert/assert-base';
import { csrfManager } from '../csrf/csrf-manager';
import { globalLogger } from '../../qaamgo/helper/global-logger';

class PaymentApi {
    /**
     * @param {Boolean} [formatWithCurrency=false]
     *
     * @return {Promise<PricingInformation>}
     */
    getPricingInformation(formatWithCurrency) {
        if (testIsUndefined(formatWithCurrency)) {
            formatWithCurrency = false;
        }

        return apiRequestManager
            .get('api/pricing-information', {
                format_with_currency: formatWithCurrency,
                _locale: satGlobals.getLocale(),
            })
            .then((response) => {
                /** @type {PricingInformation} */
                const pricingInfo = response.data;

                globalLogger.log('payment api - getPricingInformation :: success');

                return pricingInfo;
            })
            .catch((/** AxiosError */ error) => {
                globalLogger.log('payment api - getPricingInformation :: fail', error);

                return Promise.reject();
            });
    }

    /**
     * @param {'paypal'|'stripe'} provider
     * @param {Object} data
     * @param {String} data.api_id
     * @param {Object} data.data
     *
     * @return Promise
     */
    postSource(provider, data) {
        try {
            assertIsNonEmptyString(provider);
            assertIsObject(data);
            assertObjectHasKey(data, 'api_id');
        } catch (e) {
            return Promise.reject(e);
        }

        const endpoint = `/api/payment/${provider}/source`;

        return csrfManager
            .getCsrfPayment()
            .then((csrfToken) => {
                data._csrf = csrfToken;

                return apiRequestManager.postWithCaptcha(endpoint, data);
            })
            .then((response) => {
                globalLogger.log('payment api - postSource :: success');

                return Promise.resolve(response);
            })
            .catch((/** AxiosError */ error) => {
                globalLogger.log('payment api - postSource :: fail', error);

                return Promise.reject();
            });
    }

    /**
     * @param {'paypal'|'stripe'} provider
     *
     * @return Promise
     */
    postSetupIntent(provider) {
        try {
            assertIsNonEmptyString(provider);
        } catch (e) {
            return Promise.reject(e);
        }

        const endpoint = `/api/payment/${provider}/setupintent`;

        return csrfManager
            .getCsrfPayment()
            .then((csrfToken) => {
                let data = {
                    _csrf: csrfToken,
                };

                return apiRequestManager.postWithCaptcha(endpoint, data);
            })
            .then((response) => {
                globalLogger.log('payment api - postSetupIntent :: success');

                return Promise.resolve(response.data);
            })
            .catch((/** AxiosError */ error) => {
                globalLogger.log('payment api - postSetupIntent :: fail', error);

                return Promise.reject();
            });
    }

    /**
     * @param {String} provider
     * @param {String} slug
     *
     * @return Promise
     */
    postAgreement(provider, slug) {
        try {
            assertIsNonEmptyString(provider);
            assertIsNonEmptyString(slug);
        } catch (e) {
            return Promise.reject(e);
        }

        const endpoint = `/api/payment/${provider}/agreement?plan=${slug}`;

        return csrfManager
            .getCsrfPayment()
            .then((csrfToken) => {
                const data = {
                    _csrf: csrfToken,
                };

                return apiRequestManager.postWithCaptcha(endpoint, data);
            })
            .then((response) => {
                globalLogger.log('payment api - postAgreement :: success');

                return Promise.resolve(response);
            })
            .catch((/** AxiosError */ error) => {
                globalLogger.log('payment api - postAgreement :: fail', error);

                if (error?.response?.data?.message === 'user_has_already_same_product') {
                    return Promise.reject(error?.response);
                }

                return Promise.reject();
            });
    }

    /**
     * @param {String} provider
     * @param {String} slug
     *
     * @return Promise<AgreementApproval>
     */
    postAgreementApproval(provider, slug) {
        try {
            assertIsNonEmptyString(provider);
            assertIsNonEmptyString(slug);
        } catch (e) {
            return Promise.reject(e);
        }

        const endpoint = `/api/payment/${provider}/agreement/approval?plan=${slug}`;

        return csrfManager
            .getCsrfPayment()
            .then((csrfToken) => {
                const data = {
                    _csrf: csrfToken,
                };

                return apiRequestManager.postWithCaptcha(endpoint, data);
            })
            .then((response) => {
                /** @type {AgreementApproval} */
                const parsed = JSON.parse(response.data);

                globalLogger.log('payment api - postAgreementApproval :: success');

                return Promise.resolve(parsed);
            })
            .catch((/** AxiosError */ error) => {
                globalLogger.log('payment api - postAgreementApproval :: fail', error);

                if (error?.response?.data?.message === 'user_has_already_same_product') {
                    return Promise.reject(error?.response);
                }

                return Promise.reject();
            });
    }

    /**
     * @param {String} provider
     * @param {String} slug
     *
     * @return {Promise}
     */
    postOrder(provider, slug) {
        try {
            assertIsNonEmptyString(provider);
            assertIsNonEmptyString(slug);
        } catch (e) {
            return Promise.reject(e);
        }

        const endpoint = `/api/payment/${provider}/order`;

        return csrfManager
            .getCsrfPayment()
            .then((csrfToken) => {
                const data = {
                    _csrf: csrfToken,
                    order: slug,
                };

                return apiRequestManager.postWithCaptcha(endpoint, data);
            })
            .then((response) => {
                globalLogger.log('payment api - postOrder :: success');

                return Promise.resolve(response.data.order_response);
            })
            .catch((/** AxiosError */ error) => {
                globalLogger.log('payment api - postOrder :: fail', error);

                return Promise.reject();
            });
    }

    /**
     * @param {String} provider
     * @param {String} intent
     *
     * @return {Promise}
     */
    postCaptureOrder(provider, intent) {
        try {
            assertIsNonEmptyString(provider);
            assertIsNonEmptyString(intent);
        } catch (e) {
            return Promise.reject(e);
        }

        const endpoint = `/api/payment/${provider}/order/capture`;

        return csrfManager
            .getCsrfPayment()
            .then((csrfToken) => {
                const data = {
                    _csrf: csrfToken,
                    intent: intent,
                };

                return apiRequestManager.postWithCaptcha(endpoint, data);
            })
            .then((response) => {
                globalLogger.log('payment api - postCaptureOrder :: success');

                return Promise.resolve(response);
            })
            .catch((/** AxiosError */ error) => {
                globalLogger.log('payment api - postCaptureOrder :: fail', error);

                return Promise.reject();
            });
    }
}

if (!global.gPaymentApi) {
    global.gPaymentApi = new PaymentApi();
}

const paymentApi = global.gPaymentApi;

export { paymentApi };
